// A FICHA DE UM ÓRGÃO OU ENTIDADE — tudo que se sabe sobre ele, numa página.
//
// Antes, responder "o que a gente tem com esta autarquia?" custava quatro telas:
// o cadastro num modal, os convênios em `/averbadora/convenios`, a folha em
// `/averbadora/folhas`, os layouts em `/averbadora/layouts/unidade/:id` — e os
// sub-usuários do portal, em lugar nenhum do lado da averbadora. Cada uma
// filtrada à mão, e nenhuma dizendo que falavam do mesmo cliente.
//
// A ordem das abas é a ordem em que a pergunta é feita no onboarding e no
// suporte:
//
//   1. QUEM É?           dados gerais, os oficiais da Receita, as pessoas.
//   2. COM QUEM OPERA?   relações institucionais: o que ela exerce por outras e
//                        quem opera por ela, com papel, vigência e autor.
//   3. PROCESSA FOLHA?   e, se sim, como o arquivo chega, em que formato, com
//                        que layout e como o desconto encerra.
//   4. QUAIS CONVÊNIOS?  banco por banco, com a verba e o corte.
//   5. QUEM ACESSA?      o login da organização e os sub-usuários do portal.
//
// A aba mora na URL (`?aba=folha`): o suporte cola o link já na aba certa, e o
// F5 não joga ninguém de volta para a primeira.
//
// 404 é ESTADO DA TELA, não exceção: o id vem da URL, e um link velho (ou um
// cadastro recusado) cai aqui — ver `layouts-ficha.tsx`, mesmo desenho.
import { useMemo, useState, type ReactNode } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  Button,
  Card,
  DataTable,
  EstadoVazio,
  IconButton,
  Pill,
  Skeleton,
  Tabs,
  formatCnpj,
  fmtDataISO,
  type Column,
  type TabDef,
} from "@atlas/ui/web";
import { atlas } from "../../lib/sdk";
import { QUERY_UNIDADES_OPERAVEIS, listarUnidadesOperaveis } from "../../lib/unidades-operaveis";
import { LayoutChip } from "../../components/LayoutChip";
import { CadastroPessoas } from "./cadastro-pessoas";
import { OrganizacaoModal, type ModoModal } from "./organizacao-modal";
import { rotuloTipo } from "./organizacao-campos";
import { MargemInformadaPainel } from "./margem-informada-painel";
import { PainelBaseLegal } from "./base-legal-painel";
import { AcaoRelacaoModal, NovaRelacaoModal, type AcaoRelacao } from "./relacao-modal";
import { VARIANTE_SITUACAO, fmtVigencia, nomeLongoDaPonta, rotuloDoPapel } from "./relacao-campos";
import {
  CancelarRegraMargemModal,
  CorrigirServicosRegraModal,
  EncerrarRegraMargemModal,
  NovaRegraMargemModal,
  ROTULO_BASE,
  pontosDe,
  resumoDoTeto,
} from "./regra-margem-modal";
import { TabelaServicosDaRegra } from "./regra-margem-servicos";
import {
  ROTULOS,
  ROTULO_SITUACAO_RELACAO,
  type ClassificacaoAdm,
  type Esfera,
  type Poder,
  type TipoOrganizacao,
} from "@atlas/types";
import type {
  AdminConvenio,
  AdminFolha,
  AdminPrefeitura,
  RegraDeMargemDto,
  RelacaoInstitucionalDto,
} from "@atlas/sdk";

type AbaKey = "dados" | "relacoes" | "folha" | "margem" | "convenios" | "usuarios";
const ABAS: AbaKey[] = ["dados", "relacoes", "folha", "margem", "convenios", "usuarios"];

const ESTRATEGIA_ROTULO: Record<string, string> = {
  omissao: "Por omissão — a linha some do arquivo seguinte",
  codigo: "Código E — linha explícita de exclusão",
};

// ============================================================ helpers de layout

/**
 * Cartão com título — o padrão desta área (ver `layouts-ficha.tsx`).
 * `carregando` marca o cartão com `aria-busy`: o leitor de tela sabe que o
 * esqueleto não é o conteúdo, e o teste sabe qual cartão esperar.
 */
function Secao({
  titulo,
  acoes,
  carregando,
  children,
}: {
  titulo: string;
  acoes?: ReactNode;
  carregando?: boolean;
  children: ReactNode;
}) {
  return (
    <Card aria-busy={carregando ? "true" : undefined}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 12,
          flexWrap: "wrap",
          marginBottom: 10,
        }}
      >
        <h2 style={{ margin: 0, fontSize: 16 }}>{titulo}</h2>
        {acoes}
      </div>
      {children}
    </Card>
  );
}

/** Par rótulo/valor. Valor ausente vira travessão em cinza, e não some — a
 *  ausência de CNPJ é informação, e um campo que desaparece parece defeito. */
function Campo({ rotulo, children, largo }: { rotulo: string; children?: ReactNode; largo?: boolean }) {
  const vazio = children === null || children === undefined || children === "";
  return (
    <div style={{ display: "grid", gap: 3, gridColumn: largo ? "1 / -1" : undefined }}>
      <span
        style={{
          fontSize: 11,
          fontWeight: 700,
          letterSpacing: ".06em",
          color: "var(--text-dim)",
          textTransform: "uppercase",
        }}
      >
        {rotulo}
      </span>
      <span style={{ fontSize: 13, color: vazio ? "var(--text-dim)" : "var(--text)" }}>
        {vazio ? "—" : children}
      </span>
    </div>
  );
}

function GradeCampos({ children }: { children: ReactNode }) {
  return (
    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 16 }}>
      {children}
    </div>
  );
}

// ============================================================ tela

export function AdminOrganizacaoFicha() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const orgId = Number(id);

  const abaParam = params.get("aba") as AbaKey | null;
  const aba: AbaKey = abaParam && ABAS.includes(abaParam) ? abaParam : "dados";
  const trocarAba = (k: string) => {
    const p = new URLSearchParams(params);
    if (k === "dados") p.delete("aba");
    else p.set("aba", k);
    setParams(p, { replace: true });
  };

  // A MESMA queryKey da lista: abrir a ficha a partir dela não dispara request
  // nova, e salvar no modal atualiza as duas de uma vez. Uma rota
  // `GET /prefeituras/:id` traria a mesma coisa por mais código e um cache a
  // mais para invalidar.
  const lista = useQuery({
    queryKey: QUERY_UNIDADES_OPERAVEIS,
    queryFn: listarUnidadesOperaveis,
  });
  const todas = useMemo(() => lista.data?.prefeituras ?? [], [lista.data]);
  const org = todas.find((p) => p.id === orgId);
  const mae = org?.prefeituraPaiId != null ? todas.find((p) => p.id === org.prefeituraPaiId) : undefined;
  const atendidas = useMemo(() => todas.filter((p) => p.prefeituraPaiId === orgId), [todas, orgId]);

  const [editando, setEditando] = useState<ModoModal | null>(null);

  if (lista.isLoading) {
    return (
      <div style={{ display: "grid", gap: 16 }}>
        <Skeleton linhas={2} altura={28} />
        <Skeleton linhas={5} />
      </div>
    );
  }

  if (!org) {
    return (
      <EstadoVazio
        titulo="Organização não encontrada"
        orientacao={`Nenhum órgão ou entidade com o id ${id}. O cadastro pode ter sido recusado, ou o link está velho.`}
        acao={{
          rotulo: "Voltar para Órgãos e entidades",
          onClick: () => navigate("/averbadora/organizacoes"),
        }}
      />
    );
  }

  const tipo = (org.tipo ?? "prefeitura") as TipoOrganizacao;
  const abas: TabDef[] = [
    { key: "dados", label: "Dados gerais" },
    { key: "relacoes", label: "Relações institucionais" },
    { key: "folha", label: "Folha própria" },
    { key: "margem", label: "Margem" },
    { key: "convenios", label: "Convênios" },
    { key: "usuarios", label: "Usuários e acessos" },
  ];

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      {/* -------------------------------------------------- identidade */}
      <header
        style={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: 16,
          flexWrap: "wrap",
        }}
      >
        <div style={{ display: "grid", gap: 6 }}>
          <Link
            to="/averbadora/organizacoes"
            style={{
              fontSize: 12,
              letterSpacing: "0.1em",
              fontWeight: 700,
              color: "var(--text-dim)",
              textTransform: "uppercase",
            }}
          >
            ← Órgãos e entidades
          </Link>
          {/* O NOME DE EXIBIÇÃO VEM DA API, e é o mesmo que o servidor lê no
              app. Reimplementar aqui o "Prefeitura de …" seria a segunda cópia
              da regra — e foi a primeira que colou o prefixo na frente de quem
              não é prefeitura. */}
          <h1 style={{ margin: 0, fontSize: "1.6rem" }}>{org.nomeExibicao ?? org.nome}</h1>
          <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
            <Pill
              variant={
                org.status === "ativo" ? "averbado" : org.status === "inativo" ? "expirado" : "pendente"
              }
            >
              {org.status}
            </Pill>
            <Pill variant="emdia">{org.rotuloTipo ?? rotuloTipo(tipo)}</Pill>
            <Pill variant="expirado">{ROTULOS.esfera[(org.esfera ?? "municipal") as Esfera]}</Pill>
            <Pill variant="expirado">{ROTULOS.poder[(org.poder ?? "executivo") as Poder]}</Pill>
            <Pill variant="expirado">
              {ROTULOS.classificacaoAdm[(org.classificacaoAdm ?? "direta") as ClassificacaoAdm]}
            </Pill>
          </div>
          <div
            style={{ fontSize: 13, color: "var(--text-muted)", display: "flex", gap: 12, flexWrap: "wrap" }}
          >
            {org.sigla ? <span style={{ fontFamily: "var(--font-mono)" }}>{org.sigla}</span> : null}
            <span>{org.cnpj ? formatCnpj(org.cnpj) : "sem CNPJ"}</span>
            <span>
              {org.municipioNome || org.nome}
              {org.uf ? `/${org.uf}` : ""}
            </span>
            {org.municipioIbge > 0 ? (
              <span style={{ fontFamily: "var(--font-mono)" }}>IBGE {org.municipioIbge}</span>
            ) : null}
          </div>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <Button variant="ghost" onClick={() => setEditando("visualizar")}>
            Visualizar
          </Button>
          <Button onClick={() => setEditando("editar")}>Editar</Button>
        </div>
      </header>

      <Tabs tabs={abas} activeKey={aba} onChange={trocarAba} />

      {aba === "dados" ? (
        <AbaDados org={org} mae={mae} atendidas={atendidas} onEditar={() => setEditando("editar")} />
      ) : null}
      {aba === "relacoes" ? <AbaRelacoes org={org} todas={todas} /> : null}
      {aba === "folha" ? <AbaFolha org={org} mae={mae} /> : null}
      {aba === "margem" ? <AbaMargem org={org} /> : null}
      {aba === "convenios" ? <AbaConvenios org={org} /> : null}
      {aba === "usuarios" ? <AbaUsuarios org={org} onEditarLogin={() => setEditando("editar")} /> : null}

      {editando ? (
        <OrganizacaoModal initial={org} modo={editando} todas={todas} onClose={() => setEditando(null)} />
      ) : null}
    </div>
  );
}

// ============================================================ 1. dados gerais

function AbaDados({
  org,
  mae,
  atendidas,
  onEditar,
}: {
  org: AdminPrefeitura;
  mae: AdminPrefeitura | undefined;
  atendidas: AdminPrefeitura[];
  onEditar: () => void;
}) {
  const e = org.endereco ?? {};
  const logradouro = [e.logradouro, e.numero, e.complemento].filter(Boolean).join(", ");
  return (
    <div style={{ display: "grid", gap: 16 }}>
      <Secao
        titulo="Dados oficiais"
        acoes={
          <Button variant="ghost" size="sm" onClick={onEditar}>
            Editar
          </Button>
        }
      >
        {/* Somente leitura de propósito: o formulário é o do modal, e ter dois
            caminhos de gravação para o mesmo cadastro é como o telefone da
            Receita e o digitado à mão passariam a divergir. */}
        <GradeCampos>
          <Campo rotulo="Razão social">{org.razaoSocial}</Campo>
          <Campo rotulo="Nome fantasia">{org.nomeFantasia}</Campo>
          <Campo rotulo="CNPJ">{org.cnpj ? formatCnpj(org.cnpj) : null}</Campo>
          <Campo rotulo="Fundação">{org.dataFundacao ? fmtDataISO(org.dataFundacao) : null}</Campo>
          <Campo rotulo="Atividade principal">{org.atividade}</Campo>
          <Campo rotulo="Telefone">{org.telefone}</Campo>
          <Campo rotulo="E-mail de contato">{org.contatoEmail}</Campo>
          <Campo rotulo="Protocolo do cadastro">{org.protocoloCadastro}</Campo>
          <Campo rotulo="Endereço" largo>
            {logradouro
              ? `${logradouro}${e.bairro ? ` — ${e.bairro}` : ""}${e.municipio ? `, ${e.municipio}` : ""}${e.uf ? `/${e.uf}` : ""}${e.cep ? ` · CEP ${e.cep}` : ""}`
              : null}
          </Campo>
        </GradeCampos>
        {org.dadosEditadosManualmente ? (
          <div style={{ marginTop: 12, fontSize: 12, color: "var(--gold-500)" }}>
            ✎ Dados editados manualmente
            {org.dadosEditadosManualmenteEm
              ? ` em ${new Date(org.dadosEditadosManualmenteEm).toLocaleString("pt-BR")}`
              : ""}{" "}
            — podem não refletir a Receita.
          </div>
        ) : null}
      </Secao>

      <Secao titulo="Classificação institucional">
        <GradeCampos>
          <Campo rotulo="Tipo">
            {org.rotuloTipo ?? rotuloTipo((org.tipo ?? "prefeitura") as TipoOrganizacao)}
          </Campo>
          <Campo rotulo="Esfera">{ROTULOS.esfera[(org.esfera ?? "municipal") as Esfera]}</Campo>
          <Campo rotulo="Poder">{ROTULOS.poder[(org.poder ?? "executivo") as Poder]}</Campo>
          <Campo rotulo="Classificação administrativa">
            {ROTULOS.classificacaoAdm[(org.classificacaoAdm ?? "direta") as ClassificacaoAdm]}
          </Campo>
          <Campo rotulo="Sigla">{org.sigla}</Campo>
          <Campo rotulo="Município">{org.municipioNome}</Campo>
          <Campo rotulo="Nome de exibição">{org.nomeExibicao}</Campo>
        </GradeCampos>
        {/* O VÍNCULO ANTIGO, dito pelo que ele é hoje. Em US2 ele vira relação
            institucional com papel e vigência, e este bloco troca de aba. */}
        {mae || atendidas.length > 0 ? (
          <div style={{ marginTop: 14, fontSize: 13, color: "var(--text-muted)" }}>
            {mae ? (
              <div>
                Unidade pagadora de{" "}
                <Link to={`/averbadora/organizacoes/${mae.id}`}>{mae.nomeExibicao ?? mae.nome}</Link>.
              </div>
            ) : null}
            {atendidas.length > 0 ? (
              <div>
                Responsável por {atendidas.length} unidade{atendidas.length > 1 ? "s" : ""} pagadora
                {atendidas.length > 1 ? "s" : ""}:{" "}
                {atendidas.map((a, i) => (
                  <span key={a.id}>
                    {i > 0 ? ", " : ""}
                    <Link to={`/averbadora/organizacoes/${a.id}`}>{a.nomeExibicao ?? a.nome}</Link>
                  </span>
                ))}
                .
              </div>
            ) : null}
          </div>
        ) : null}
      </Secao>

      {/* As pessoas do cadastro salvam direto na API, fora do modal — o mesmo
          componente que a esteira de aprovação usa. */}
      <Secao titulo="Pessoas do cadastro">
        <CadastroPessoas kind="prefeituras" tenantId={org.id} somenteLeitura />
      </Secao>
    </div>
  );
}

// ============================================================ 2. relações

/**
 * AS DUAS LISTAS, e por que são duas e não uma com uma coluna "sentido".
 *
 * "Exerce" e "Recebe" respondem perguntas diferentes e levam a decisões
 * diferentes: a primeira é o que esta organização faz pelos outros (e o que ela
 * perde se a relação for encerrada); a segunda é quem tem acesso aos dados
 * DELA — que é a pergunta do jurídico e a do próprio cliente. Uma tabela só,
 * ordenada por qualquer coisa, misturaria as duas leituras linha a linha.
 *
 * O histórico vem inteiro, com as encerradas no fim — a ordem é da API. "Quem
 * respondia por isto antes?" é pergunta de suporte, e escondê-la exigiria um
 * segundo pedido que a tela não tem como saber que precisa fazer.
 */
function AbaRelacoes({ org, todas }: { org: AdminPrefeitura; todas: AdminPrefeitura[] }) {
  const q = useQuery({
    queryKey: ["admin", "relacoes", "organizacao", org.id],
    queryFn: () => atlas.admin.relacoesDaOrganizacao(org.id),
  });

  const [criando, setCriando] = useState<"exerce" | "recebe" | null>(null);
  const [agindo, setAgindo] = useState<{ relacao: RelacaoInstitucionalDto; acao: AcaoRelacao } | null>(null);

  const colunas = (lado: "exerce" | "recebe"): Column<RelacaoInstitucionalDto>[] => [
    {
      key: "organizacao",
      header: lado === "exerce" ? "Organização atendida" : "Organização responsável",
      render: (r) => {
        const outra = lado === "exerce" ? r.atendida : r.responsavel;
        return (
          <Link
            to={`/averbadora/organizacoes/${outra.id}?aba=relacoes`}
            style={{ color: "inherit", fontWeight: 600 }}
          >
            {nomeLongoDaPonta(outra)}
          </Link>
        );
      },
    },
    { key: "papel", header: "Papel", render: (r) => r.rotuloPapel || rotuloDoPapel(r.papel) },
    { key: "vigencia", header: "Vigência", mono: true, render: (r) => fmtVigencia(r) },
    {
      key: "situacao",
      header: "Situação",
      render: (r) => (
        <div style={{ display: "grid", gap: 4, justifyItems: "start" }}>
          <Pill variant={VARIANTE_SITUACAO[r.situacao]}>{ROTULO_SITUACAO_RELACAO[r.situacao]}</Pill>
        </div>
      ),
    },
    {
      key: "justificativa",
      header: "Justificativa",
      wrap: true,
      render: (r) => (
        <span style={{ fontSize: 12, color: "var(--text-muted)" }}>
          {r.suspensaMotivo ? `suspensa: ${r.suspensaMotivo}` : r.justificativa}
        </span>
      ),
    },
    {
      key: "autor",
      header: "Autor",
      render: (r) => (
        <div style={{ display: "grid", gap: 2 }}>
          <span style={{ fontSize: 12 }}>{r.atualizadaPor ?? r.criadaPor}</span>
          <span style={{ fontSize: 11, color: "var(--text-dim)" }}>
            {(r.atualizadaEm ?? r.criadaEm)
              ? new Date(r.atualizadaEm ?? r.criadaEm).toLocaleDateString("pt-BR")
              : "—"}
          </span>
        </div>
      ),
    },
  ];

  const acoes = (r: RelacaoInstitucionalDto) => (
    <>
      {r.situacao !== "encerrada" ? (
        <IconButton title="Encerrar" danger onClick={() => setAgindo({ relacao: r, acao: "encerrar" })}>
          ⏹
        </IconButton>
      ) : null}
      {r.suspensaEm ? (
        <IconButton title="Reativar" onClick={() => setAgindo({ relacao: r, acao: "reativar" })}>
          ▶
        </IconButton>
      ) : r.situacao !== "encerrada" ? (
        <IconButton title="Suspender" onClick={() => setAgindo({ relacao: r, acao: "suspender" })}>
          ⏸
        </IconButton>
      ) : null}
      <IconButton title="Corrigir vigência" onClick={() => setAgindo({ relacao: r, acao: "corrigir" })}>
        ✎
      </IconButton>
    </>
  );

  const nome = org.nomeExibicao ?? org.nome;

  return (
    <div style={{ display: "grid", gap: 16 }}>
      <Secao
        titulo="Exerce"
        carregando={q.isLoading}
        acoes={
          <Button size="sm" onClick={() => setCriando("exerce")}>
            + Nova relação
          </Button>
        }
      >
        <p style={{ margin: "0 0 10px", fontSize: 13, color: "var(--text-muted)" }}>
          O que {nome} faz por outras organizações. A relação <b>dá acesso; não move propriedade</b>: os dados
          continuam de quem é atendido.
        </p>
        <DataTable
          columns={colunas("exerce")}
          caption={`Relações que ${nome} exerce`}
          rows={q.data?.exerce ?? []}
          rowKey={(r) => (r.id != null ? `r${r.id}` : `d${r.responsavelId}-${r.atendidaId}-${r.papel}`)}
          loading={q.isLoading}
          emptyState={`${nome} não exerce papel nenhum por outra organização.`}
          actions={acoes}
        />
      </Secao>

      <Secao
        titulo="Recebe"
        carregando={q.isLoading}
        acoes={
          <Button size="sm" onClick={() => setCriando("recebe")}>
            + Nova relação
          </Button>
        }
      >
        <p style={{ margin: "0 0 10px", fontSize: 13, color: "var(--text-muted)" }}>
          Quem opera por {nome} — e, portanto, quem alcança os dados desta organização no portal.
        </p>
        <DataTable
          columns={colunas("recebe")}
          caption={`Relações que ${nome} recebe`}
          rows={q.data?.recebe ?? []}
          rowKey={(r) => (r.id != null ? `r${r.id}` : `d${r.responsavelId}-${r.atendidaId}-${r.papel}`)}
          loading={q.isLoading}
          emptyState={`Ninguém opera por ${nome}: ela responde só por si.`}
          actions={acoes}
        />
      </Secao>

      <p style={{ margin: 0, fontSize: 12, color: "var(--text-dim)" }}>
        Relação não se exclui: encerrar grava a competência final e ela continua no histórico, porque a folha
        que ela cobriu continua existindo. Ver <Link to="/averbadora/relacoes">todas as relações</Link>.
      </p>

      {/* A ponta que a ficha conhece já vai preenchida — e a outra fica em
          aberto, que é o único campo que quem clicou "Nova relação" aqui ainda
          precisa decidir. */}
      {criando ? (
        <NovaRelacaoModal
          organizacoes={todas}
          responsavelInicial={criando === "exerce" ? org.id : undefined}
          atendidaInicial={criando === "recebe" ? org.id : undefined}
          onClose={() => setCriando(null)}
        />
      ) : null}
      {agindo ? (
        <AcaoRelacaoModal relacao={agindo.relacao} acao={agindo.acao} onClose={() => setAgindo(null)} />
      ) : null}
    </div>
  );
}

// ============================================================ 3. folha própria

function AbaFolha({ org, mae }: { org: AdminPrefeitura; mae: AdminPrefeitura | undefined }) {
  // `enabled` e nao um `if` antes do hook: quem nao processa folha nao tem
  // competencia nenhuma, e buscar a lista inteira de folhas do sistema para
  // descartar tudo e' trabalho que a resposta ja disse ser inutil.
  const folhasQ = useQuery({
    queryKey: ["admin", "folhas"],
    queryFn: () => atlas.admin.listFolhas(),
    enabled: org.temFolhaPropria !== false,
  });
  const folhas = (folhasQ.data?.folhas ?? []).filter((f) => f.unidadeId === org.id);

  // NÃO PROCESSA FOLHA: mostrar corte, partes e layout aqui seria oferecer
  // configuração que não chega a lugar nenhum. `temFolhaPropria` é derivado pela
  // API (tem servidor, convênio ou competência); ausente = resposta anterior ao
  // campo, e aí não se afirma nada.
  if (org.temFolhaPropria === false) {
    return (
      <Secao titulo="Folha própria">
        <EstadoVazio
          titulo="Este órgão não processa folha própria"
          orientacao={
            mae ? (
              <>
                Os servidores dele são descontados dentro da folha de{" "}
                <Link to={`/averbadora/organizacoes/${mae.id}`}>{mae.nomeExibicao ?? mae.nome}</Link>, e é lá
                que ficam o corte, o layout e as partes do retorno.
              </>
            ) : (
              <>
                Não há servidor, convênio nem competência nesta organização. Enquanto for assim, corte, layout
                e partes do retorno não se aplicam — quando o primeiro convênio ou a primeira remessa entrar,
                esta aba passa a mostrá-los.
              </>
            )
          }
        />
      </Secao>
    );
  }

  const colunas: Column<AdminFolha>[] = [
    { key: "competencia", header: "Competência", mono: true },
    {
      key: "status",
      header: "Situação",
      render: (f) => (
        <Pill
          variant={f.status === "consolidada" ? "averbado" : f.status === "fechada" ? "emdia" : "pendente"}
        >
          {f.status}
        </Pill>
      ),
    },
    { key: "dataCorte", header: "Corte", render: (f) => fmtDataISO(f.dataCorte) },
    { key: "dataRepasse", header: "Repasse", render: (f) => fmtDataISO(f.dataRepasse) },
    {
      key: "conferencia",
      header: "Conferência",
      // "Presumida" é o estado que precisa saltar: a competência fechou pelo
      // PRAZO sem o retorno completo, e ninguém confirmou os descontos.
      render: (f) =>
        f.conferencia === "presumida" ? (
          <Pill variant="pendente">presumida</Pill>
        ) : f.conferencia === "confirmada" ? (
          <Pill variant="aceita">confirmada</Pill>
        ) : (
          <span style={{ color: "var(--text-dim)" }}>—</span>
        ),
    },
    {
      key: "adfsAplicadas",
      header: "ADFs aplicadas",
      align: "right",
      render: (f) => (f.adfsAplicadas ?? 0).toLocaleString("pt-BR"),
    },
  ];

  return (
    <div style={{ display: "grid", gap: 16 }}>
      <Secao titulo="Como o arquivo chega">
        <GradeCampos>
          <Campo rotulo="Modo de integração">{org.modoIntegracao}</Campo>
          <Campo rotulo="URL do CSV da folha">{org.folhaSincUrl}</Campo>
          <Campo rotulo="Servidores cadastrados">{org.servidoresCount.toLocaleString("pt-BR")}</Campo>
          <Campo rotulo="Última sincronização">
            {org.ultimaSincronizacao ? new Date(org.ultimaSincronizacao).toLocaleString("pt-BR") : null}
          </Campo>
          {org.ultimaSincResultado ? (
            <Campo rotulo="Resultado da última sync" largo>
              {org.ultimaSincResultado.erro ? (
                <span style={{ color: "var(--danger-500)" }}>{org.ultimaSincResultado.erro}</span>
              ) : (
                `${org.ultimaSincResultado.novos} novos, ${org.ultimaSincResultado.atualizados} atualizados`
              )}
            </Campo>
          ) : null}
        </GradeCampos>
      </Secao>

      <Secao
        titulo="Formato declarado e layouts"
        acoes={<Link to={`/averbadora/layouts/unidade/${org.id}`}>abrir o hub de layouts →</Link>}
      >
        {/* A COBERTURA NÃO É REPETIDA AQUI de propósito: o painel da unidade
            (feature 008) já responde "ela consegue entrar?" com o motivo e as
            pendências, e uma segunda leitura do mesmo estado é a próxima a
            divergir. Aqui ficam a identidade do layout e o formato declarado. */}
        <GradeCampos>
          <Campo rotulo="Formato declarado">
            {org.formatoDeclarado
              ? `${org.formatoDeclarado.padrao ? "Padrão Atlas" : "Fora do padrão"} — ${org.formatoDeclarado.descricao}`
              : null}
          </Campo>
          <Campo rotulo="Declarado por">
            {org.formatoDeclarado
              ? `${org.formatoDeclarado.declaradoPor} em ${new Date(org.formatoDeclarado.declaradoEm).toLocaleString("pt-BR")}`
              : null}
          </Campo>
        </GradeCampos>
        <div style={{ marginTop: 14 }}>
          {org.layouts ? (
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
              {(["entrada", "saida"] as const).map((s) => {
                const l = org.layouts![s];
                return (
                  <div key={s} style={{ display: "grid", gap: 4 }}>
                    <span
                      style={{
                        fontSize: 11,
                        color: "var(--text-dim)",
                        textTransform: "uppercase",
                        letterSpacing: ".06em",
                      }}
                    >
                      {s === "entrada" ? "Retorno" : "Movimento"}
                    </span>
                    <LayoutChip
                      nome={l.nome}
                      codigo={l.codigo}
                      versao={l.versao}
                      situacao={l.situacao}
                      padrao={l.padrao}
                    />
                  </div>
                );
              })}
            </div>
          ) : (
            <span style={{ fontSize: 13, color: "var(--text-dim)" }}>
              Nenhum layout atribuído a esta unidade.
            </span>
          )}
        </div>
      </Secao>

      <Secao titulo="Corte e repasse">
        {/* A FONTE ÚNICA das datas da folha (ADR-0012). Até 15/09/2026 estes
            dois dias eram atributo do convênio, um por banco, e a folha usava o
            menor entre eles — a tabela de convênios logo abaixo continua
            mostrando os dias, agora herdados daqui. */}
        <GradeCampos>
          <Campo rotulo="Corte">
            {org.corteRegra
              ? `dia ${org.corteRegra.dia}, ${org.corteRegra.hora} · ${org.corteRegra.seNaoUtil === "anterior" ? "antecipa" : "adia"} quando cai em fim de semana`
              : "dia 15, 17:00 · antecipa quando cai em fim de semana (padrão)"}
          </Campo>
          <Campo rotulo="Repasse">
            {org.corteRegra?.diaRepasse
              ? `dia ${org.corteRegra.diaRepasse} do mês seguinte à competência`
              : `no próprio dia do corte, no mês seguinte (padrão)`}
          </Campo>
        </GradeCampos>
      </Secao>

      <Secao titulo="Encerramento e trava">
        <GradeCampos>
          <Campo rotulo="Como o desconto encerra">
            {ESTRATEGIA_ROTULO[org.estrategiaEncerramento ?? "omissao"]}
          </Campo>
          <Campo rotulo="Trava de margem">
            {org.travaAtiva ? (
              <span style={{ color: "var(--emerald-500)" }}>ligada</span>
            ) : (
              <span style={{ color: "var(--text-dim)" }}>desligada</span>
            )}
            {" · "}
            <Link to="/averbadora/trava-margem">monitor</Link>
          </Campo>
          <Campo rotulo="Senha do servidor na averbação pela consignatária">
            {org.exigeSenhaAverbacao === false ? "Não exige" : "Exige"}
          </Campo>
          <Campo rotulo="Servidor pode editar contato pelo app">
            {org.permiteServidorEditarContato ? "Sim" : "Não"}
          </Campo>
        </GradeCampos>
      </Secao>

      <Secao
        titulo="Competências"
        carregando={folhasQ.isLoading}
        acoes={<Link to="/averbadora/folhas">todas as folhas →</Link>}
      >
        <DataTable
          columns={colunas}
          caption="Competências desta organização"
          rows={folhas}
          rowKey={(f) => f.id}
          loading={folhasQ.isLoading}
          emptyState="Nenhuma competência aberta para esta organização."
        />
      </Secao>
    </div>
  );
}

// ============================================================ 3.5. margem

/**
 * A MARGEM É REGRA DO ENTE (ADR-0012), e esta aba é onde a lei dele entra.
 *
 * Até 15/09/2026 o sistema aplicava 35% de empréstimo e 5% de cada cartão a
 * TODA prefeitura — uma constante no código. A Lei 2.231/2024 de Morada Nova
 * manda 40/5/15 com o somatório do crédito consignado limitado a 60%, e a
 * Capistrano manda a margem pronta no arquivo. Uma constante não atende as três.
 *
 * DUAS COISAS QUE A TELA PRECISA DIZER SEM RODEIO:
 *
 *   1. QUANDO NÃO HÁ REGRA, O QUE VALE É O PADRÃO — e isso aparece escrito. Um
 *      "35%" solto na tela parece decisão de alguém sobre esta organização;
 *      dizer "padrão do sistema" é a diferença entre conferir e ir procurar
 *      quem decidiu.
 *   2. NADA HERDA (ADR-0008). A lei do município alcança a administração
 *      indireta, mas a autarquia precisa da regra dela — e a ficha da autarquia
 *      vai continuar mostrando o padrão até alguém cadastrar.
 *
 * O histórico vem inteiro, com as encerradas no fim: "qual era a margem quando
 * este contrato foi averbado?" é pergunta de auditoria.
 */
function AbaMargem({ org }: { org: AdminPrefeitura }) {
  const q = useQuery({
    queryKey: ["admin", "regra-margem", "organizacao", org.id],
    queryFn: () => atlas.admin.regraMargemDaOrganizacao(org.id),
  });

  const [criando, setCriando] = useState(false);
  const [encerrando, setEncerrando] = useState<RegraDeMargemDto | null>(null);
  const [cancelando, setCancelando] = useState<RegraDeMargemDto | null>(null);
  const [corrigindoServicos, setCorrigindoServicos] = useState<RegraDeMargemDto | null>(null);

  const pct = (f: number | null) => (f == null ? "—" : `${pontosDe(f)}%`);

  const colunas: Column<RegraDeMargemDto>[] = [
    { key: "vigencia", header: "Vigência", mono: true, render: (r) => fmtVigencia(r) },
    {
      key: "percentuais",
      header: "Empréstimo / Cartão / Benefício",
      mono: true,
      render: (r) =>
        `${pct(r.percentuais.EMPRESTIMO)} / ${pct(r.percentuais.CARTAO_CONSIGNADO)} / ${pct(
          r.percentuais.CARTAO_BENEFICIOS,
        )}`,
    },
    { key: "total", header: "Teto somado", mono: true, render: (r) => pct(r.percentualTotal) },
    {
      key: "situacao",
      header: "Situação",
      render: (r) => (
        <Pill
          variant={
            r.situacao === "vigente"
              ? "aceita"
              : r.situacao === "agendada"
                ? "pendente"
                : r.situacao === "cancelada"
                  ? "rejeitada"
                  : "expirado"
          }
        >
          {r.situacao === "vigente"
            ? "Vigente"
            : r.situacao === "agendada"
              ? "Agendada"
              : r.situacao === "cancelada"
                ? "Cancelada"
                : "Encerrada"}
        </Pill>
      ),
    },
    {
      key: "fundamento",
      header: "Fundamento",
      wrap: true,
      render: (r) => (
        <div style={{ display: "grid", gap: 2 }}>
          <span style={{ fontSize: 12 }}>{r.fundamento}</span>
          <span style={{ fontSize: 11, color: "var(--text-dim)" }}>{r.justificativa}</span>
        </div>
      ),
    },
    {
      key: "autor",
      header: "Autor",
      render: (r) => (
        <div style={{ display: "grid", gap: 2 }}>
          <span style={{ fontSize: 12 }}>{r.atualizadaPor ?? r.criadaPor}</span>
          <span style={{ fontSize: 11, color: "var(--text-dim)" }}>
            {fmtDataISO(r.atualizadaEm ?? r.criadaEm)}
          </span>
        </div>
      ),
    },
  ];

  const vigente = q.data?.vigente;

  return (
    <div style={{ display: "grid", gap: 16 }}>
      <MargemInformadaPainel org={org} />
      <Secao
        titulo="Regra percentual calculada"
        carregando={q.isLoading}
        acoes={<Button onClick={() => setCriando(true)}>Cadastrar regra</Button>}
      >
        {q.isLoading ? (
          <Skeleton linhas={2} altura={28} />
        ) : vigente ? (
          <div style={{ display: "grid", gap: 12 }}>
            {vigente.padrao ? (
              <div
                style={{
                  border: "1px solid var(--border)",
                  borderRadius: 8,
                  padding: "10px 12px",
                  fontSize: 13,
                  color: "var(--text-muted)",
                }}
              >
                Nenhuma regra cadastrada para esta organização: vale o <strong>padrão do sistema</strong>.
                Cadastrar uma regra não altera nenhuma outra organização.
              </div>
            ) : null}
            <GradeCampos>
              <Campo rotulo="Empréstimo">{pct(vigente.percentuais.EMPRESTIMO)}</Campo>
              <Campo rotulo="Cartão de crédito consignado">
                {pct(vigente.percentuais.CARTAO_CONSIGNADO)}
              </Campo>
              <Campo rotulo="Cartão de benefício">{pct(vigente.percentuais.CARTAO_BENEFICIOS)}</Campo>
              <Campo rotulo="Teto somado do crédito">
                {vigente.percentualTotal == null ? "Sem teto somado" : pct(vigente.percentualTotal)}
              </Campo>
              <Campo rotulo="Base de cálculo">
                {ROTULO_BASE[vigente.base] ?? vigente.base}
              </Campo>
              <Campo rotulo="Teto global da emissão">{resumoDoTeto(vigente.politicaDeCorte)}</Campo>
              <Campo rotulo="Competência">{q.data?.competencia}</Campo>
            </GradeCampos>
            {/* #271: o que cada tipo de serviço consome NESTA regra. Fixture
                anterior à #271 vem sem `servicos`: a tabela cai no catálogo. */}
            <TabelaServicosDaRegra servicos={vigente.servicos ?? []} />
          </div>
        ) : null}
      </Secao>

      {/* #273: avisa, nunca trava — base informada x base legal, e o que falta. */}
      <PainelBaseLegal unidadeId={org.id} />

      <Secao titulo="Histórico de regras" carregando={q.isLoading}>
        {q.isLoading ? (
          <Skeleton linhas={4} />
        ) : (q.data?.historico.length ?? 0) === 0 ? (
          <EstadoVazio
            titulo="Sem histórico de regras"
            orientacao="Enquanto não houver regra, a organização segue no padrão do sistema."
          />
        ) : (
          <DataTable
            columns={colunas}
            rows={q.data!.historico}
            rowKey={(r) => String(r.id)}
            caption="Regras de margem desta organização"
            actions={(r: RegraDeMargemDto) =>
              r.situacao === "encerrada" || r.situacao === "cancelada" ? null : (
                <>
                  {/* #271: o que cada serviço consome, na vigente e na agendada. */}
                  <IconButton title="Serviços" onClick={() => setCorrigindoServicos(r)}>
                    ☰
                  </IconButton>
                  {r.situacao === "agendada" ? (
                    // Agendada nunca valeu: pode ser cancelada. A vigente só se encerra.
                    <IconButton title="Cancelar" danger onClick={() => setCancelando(r)}>
                      ✕
                    </IconButton>
                  ) : (
                    <IconButton title="Encerrar" danger onClick={() => setEncerrando(r)}>
                      ⏹
                    </IconButton>
                  )}
                </>
              )
            }
          />
        )}
      </Secao>

      <NovaRegraMargemModal org={org} aberto={criando} onFechar={() => setCriando(false)} />
      <EncerrarRegraMargemModal
        regra={encerrando}
        aberto={encerrando != null}
        onFechar={() => setEncerrando(null)}
      />
      <CancelarRegraMargemModal
        regra={cancelando}
        aberto={cancelando != null}
        onFechar={() => setCancelando(null)}
      />
      <CorrigirServicosRegraModal
        regra={corrigindoServicos}
        aberto={corrigindoServicos != null}
        onFechar={() => setCorrigindoServicos(null)}
      />
    </div>
  );
}

// ============================================================ 4. convênios

function AbaConvenios({ org }: { org: AdminPrefeitura }) {
  const q = useQuery({ queryKey: ["admin", "convenios"], queryFn: () => atlas.admin.listConvenios() });
  const convenios = (q.data?.convenios ?? []).filter((c) => c.unidadeId === org.id);

  const colunas: Column<AdminConvenio>[] = [
    {
      key: "bancoNome",
      header: "Consignatária",
      // Banco null = FK órfã (banco deletado). Vermelho porque o convênio
      // continua originando contrato e ninguém sabe para quem.
      render: (c) => c.bancoNome ?? <span style={{ color: "var(--danger-500)" }}>consignatária removida</span>,
    },
    { key: "nome", header: "Convênio" },
    {
      key: "codigoVerba",
      header: "Verba",
      mono: true,
      render: (c) => c.codigoVerba || <span style={{ color: "var(--text-dim)" }}>—</span>,
    },
    { key: "dataCorte", header: "Dia de corte", align: "right" },
    { key: "diaRepasse", header: "Dia de repasse", align: "right" },
    {
      key: "ativo",
      header: "Situação",
      render: (c) => <Pill variant={c.ativo ? "averbado" : "expirado"}>{c.ativo ? "ativo" : "inativo"}</Pill>,
    },
  ];

  return (
    <Secao
      titulo="Convênios"
      carregando={q.isLoading}
      acoes={<Link to="/averbadora/convenios">gerenciar convênios →</Link>}
    >
      <DataTable
        columns={colunas}
        caption="Convênios desta organização"
        rows={convenios}
        rowKey={(c) => c.id}
        loading={q.isLoading}
        emptyState="Nenhum convênio nesta organização. Sem convênio, nenhuma consignatária pode originar contrato aqui."
      />
    </Secao>
  );
}

// ============================================================ 5. usuários

type PerfilDaOrganizacao = Awaited<ReturnType<typeof atlas.admin.organizacaoPerfis>>["perfis"][number];

function AbaUsuarios({ org, onEditarLogin }: { org: AdminPrefeitura; onEditarLogin: () => void }) {
  const q = useQuery({
    queryKey: ["admin", "organizacao-perfis", org.id],
    queryFn: () => atlas.admin.organizacaoPerfis(org.id),
  });
  const perfis = q.data?.perfis ?? [];
  const presets = new Map((q.data?.presets ?? []).map((p) => [p.key, p.nome]));

  const colunas: Column<PerfilDaOrganizacao>[] = [
    { key: "nome", header: "Nome" },
    { key: "email", header: "E-mail" },
    {
      key: "perfil",
      header: "Perfil",
      // Preset só quando o usuário foi ATRIBUÍDO a ele — não por coincidência de
      // permissões, senão dois usuários diferentes apareceriam colados.
      render: (p) => (p.presetKey ? (presets.get(p.presetKey) ?? p.presetKey) : p.area || "Personalizado"),
    },
    {
      key: "temSenha",
      header: "Senha",
      render: (p) =>
        p.temSenha ? (
          <span style={{ color: "var(--emerald-500)" }}>✓ definida</span>
        ) : (
          <span
            style={{ color: "var(--text-dim)" }}
            title="Sem senha — este sub-usuário ainda não consegue entrar."
          >
            — não definida
          </span>
        ),
    },
    {
      key: "twofaEnabled",
      header: "2FA",
      render: (p) => (p.twofaEnabled ? "ligado" : <span style={{ color: "var(--text-dim)" }}>—</span>),
    },
    {
      key: "ativo",
      header: "Situação",
      render: (p) => <Pill variant={p.ativo ? "averbado" : "expirado"}>{p.ativo ? "ativo" : "inativo"}</Pill>,
    },
    { key: "criadoEm", header: "Criado em", render: (p) => new Date(p.criadoEm).toLocaleDateString("pt-BR") },
  ];

  return (
    <div style={{ display: "grid", gap: 16 }}>
      <Secao
        titulo="Login da organização"
        acoes={
          <Button variant="ghost" size="sm" onClick={onEditarLogin}>
            Editar credenciais
          </Button>
        }
      >
        {/* QUALQUER organização tem login — não só prefeitura. Uma unidade
            pagadora vinculada é a exceção, e por desenho: quem opera por ela é o
            login da responsável, escolhendo a unidade na troca de arquivos. */}
        {org.prefeituraPaiId != null ? (
          <p style={{ margin: 0, fontSize: 13, color: "var(--text-muted)" }}>
            Esta é uma unidade pagadora vinculada e <b>não tem login próprio</b>. O acesso é pelo login da
            organização responsável, que a seleciona na troca de arquivos.
          </p>
        ) : (
          <GradeCampos>
            <Campo rotulo="E-mail de login">{org.loginEmail}</Campo>
            <Campo rotulo="Acesso do master">
              {org.hasPassword ? (
                <span style={{ color: "var(--emerald-500)" }}>✓ ativado</span>
              ) : (
                <span style={{ color: "var(--gold-500)" }}>aguardando primeiro acesso</span>
              )}
            </Campo>
          </GradeCampos>
        )}
      </Secao>

      <Secao titulo="Sub-usuários do portal" carregando={q.isLoading}>
        {/* SOMENTE LEITURA, e a tela diz por quê: criar e desativar sub-usuário
            continua sendo ato da própria organização, no portal dela. O que a
            averbadora ganhou foi conseguir responder "quem tem acesso?" sem
            pedir a senha do cliente. */}
        <p style={{ margin: "0 0 12px", fontSize: 12, color: "var(--text-muted)" }}>
          Criados e desativados pela própria organização, em Usuários no portal dela. Aqui é consulta.
        </p>
        <DataTable
          columns={colunas}
          caption="Sub-usuários do portal desta organização"
          rows={perfis}
          rowKey={(p) => String(p.id)}
          loading={q.isLoading}
          emptyState="Nenhum sub-usuário. Quem acessa o portal é o login da organização."
        />
      </Secao>
    </div>
  );
}
